"use client";

// Stage funnel — GET /api/admin/pipeline.

import { adminApi, type AdminPipeline } from "../lib/api";
import { PIPELINE_STAGE_COUNT, pipelineStageRows } from "../lib/adminMap";
import { useApi } from "../lib/useApi";
import Reveal from "./Reveal";
import { Skeleton } from "./Skeleton";
import Bar, { barBlend } from "./Bar";
import { PanelEmpty, PanelError } from "./PanelState";

export default function DealPipeline() {
  const { data, loading, error, reload } = useApi<AdminPipeline>(
    (signal) => adminApi.pipeline(signal),
    [],
    "Failed to load the pipeline.",
  );

  const rows = data ? pipelineStageRows(data) : [];
  const max = Math.max(...rows.map((s) => s.count), 0) || 1;

  return (
    <Reveal>
    <div className="neu p-5 sm:p-6 flex flex-col h-full">
      <div className="mb-5 flex items-end justify-between gap-3">
        <div>
          <h2 className="font-display text-lg text-text">Deal Pipeline</h2>
          <p className="text-[12.5px] text-text-faint">Every deal by current stage</p>
        </div>
        {/* Placeholder keeps the header height stable while loading. */}
        {loading ? (
          <Skeleton className="h-3.5 w-40 rounded" />
        ) : data ? (
          <span className="text-[12px] text-text-faint tabular-nums">
            {data.waitingForManager} waiting · {data.escalated} escalated
          </span>
        ) : null}
      </div>

      {loading ? (
        // Same three-column row as the real bars (label · track · count) so
        // nothing shifts sideways when the stages arrive. The tracks hold real
        // Bars at zero, so the stages grow in rather than replacing a ghost.
        <div className="flex flex-col gap-3 flex-1 justify-center">
          {Array.from({ length: PIPELINE_STAGE_COUNT }).map((_, i) => (
            <div key={i} className="flex items-center gap-3">
              <Skeleton className="w-36 sm:w-40 shrink-0 h-3.5 rounded" />
              <div className="flex-1">
                <Bar fraction={0} loading color={barBlend(i, PIPELINE_STAGE_COUNT)} />
              </div>
              <Skeleton className="w-10 shrink-0 h-3.5 rounded" />
            </div>
          ))}
        </div>
      ) : error ? (
        <PanelError message={error} onRetry={reload} />
      ) : rows.length === 0 ? (
        <PanelEmpty title="No deals in the pipeline" hint="Stages populate as salespeople log customers." icon="handshake" />
      ) : (
        <div className="flex flex-col gap-3 flex-1 justify-center">
          {rows.map((s, i) => (
            <div key={s.stage} className="flex items-center gap-3">
              <span className="w-36 sm:w-40 shrink-0 text-[12.5px] font-medium text-text-muted truncate">
                {s.stage}
              </span>
              <div className="flex-1">
                {/* Position along the funnel drives the accent → secondary blend. */}
                <Bar fraction={s.count / max} color={barBlend(i, rows.length)} />
              </div>
              <span className="w-10 shrink-0 text-right text-[13px] font-semibold text-text tabular-nums">
                {s.count}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
    </Reveal>
  );
}
